import { ChangeDetectorRef, Component, Inject, Input, OnChanges, PLATFORM_ID } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SpecialModule } from 'lib/modules/special.module';
import { CmsPartialComponentClass } from 'lib/components/cms/cms-partial-component.class';
import { CategoryService } from 'lib/services/category/category.service';
import { RouterService } from 'lib/services/router.service';
import { WebpService } from 'lib/services/webp.service';
import { Location } from '@angular/common';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { BreakpointObserver } from '@angular/cdk/layout';
import get from 'lodash-es/get';
import { getRelatedBlogPosts } from 'lib/tools';
import { CmsService } from 'lib/services/cms.service';
import { ButtonComponent } from '../../../../../components/button/button.component';
import { ResourceCardRelatedCardComponent } from '../../../related-content-tile/resource-card-related-card.component';
import { readTimePipe } from 'lib/pipes/readTime.pipe';

const scriptName = 'cms-partial-category-top-component-related-content.component';

@Component({
    selector: 'cms-partial-category-top-component-related-content',
    templateUrl: './related-content.component.html',
    styleUrls: ['./related-content.component.scss'],
    standalone: true,
    imports: [CommonModule, RouterModule, SpecialModule, ButtonComponent, ResourceCardRelatedCardComponent, readTimePipe],
})
export class RelatedContentComponent extends CmsPartialComponentClass implements OnChanges {
    @Input() relatedContentRow: any;
    getWebpImg: (src: string) => string;
    activeCategoryName: string = '';
    recommendedBlogPosts: any = [];

    constructor(
        @Inject('environment') environment: any,
        breakpointObserver: BreakpointObserver,
        cmsService: CmsService,
        routerService: RouterService,
        webpService: WebpService,
        location: Location,
        categoryService: CategoryService,
        private cdr: ChangeDetectorRef,
        @Inject(PLATFORM_ID) platformId: object
    ) {
        super({
            dependencies: {
                platformId,
                breakpointObserver,
                cmsService,
                environment,
                routerService,
                location,
                categoryService,
                webpService,
            },
        });
        this.getWebpImg = (src: string) => webpService.getWebpImg(src);
    }
    async ngOnChanges(changes: any) {
        try {
            if (!this.relatedContentRow && changes && changes.relatedContentRow && changes.relatedContentRow.currentValue) {
                this.relatedContentRow = changes.relatedContentRow.currentValue;
            }
            const rows = get(changes, 'relatedContentRow.currentValue.rows', []);

            // Browser only: the post query returns up to 100 full posts (~2 MB, serialized into the
            // SSR page twice), and the active tab depends on them, so the server renders the empty shell
            if (this.isBrowser && this.recommendedBlogPosts && this.recommendedBlogPosts.length === 0 && rows.length > 0) {
                let relatedContent = null;
                let blogTags = null;
                // go through category top rows and find related content
                rows.forEach((r) => {
                    if (Object.keys(r)[0] === 'related_content') {
                        relatedContent = get(r, 'related_content.related_content[0]', r);
                        blogTags = get(r, 'related_content.related_content[0].blog.blog_tags', r);
                    }
                });
                if (relatedContent) {
                    const posts = await getRelatedBlogPosts({
                        entry: relatedContent,
                        cmsService: this.cmsService,
                        environment: this.environment,
                        tags: blogTags,
                        limit: 100,
                    });
                    posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                    this.recommendedBlogPosts = posts;
                    this.cdr.markForCheck(); // Trigger change detection with OnPush
                }

                // Set the active category to the first available category
                if (!this.activeCategoryName) {
                    const availableCategories = this.categories;
                    if (availableCategories.length > 0) {
                        this.activeCategoryName = availableCategories[0];
                        this.cdr.markForCheck(); // Trigger change detection with OnPush
                    }
                }
            }
        } catch (err) {
            console.error(...new ExxComError(999441, scriptName, err).stamp());
        }
    }
    openTagSearch(tag: string, event: Event) {
        try {
            event.stopPropagation();
            event.preventDefault();
            const pathSegment = window.location.pathname.split('/')[1];
            window.open(`${window.location.origin}/${pathSegment}?type=tag&value=${encodeURIComponent(tag)}`, '_blank');
        } catch (err) {
            console.error(...new ExxComError(484998, scriptName, err).stamp());
        }
    }

    onChosenTitle(title: string) {
        try {
            this.activeCategoryName = title;
            this.cdr.markForCheck(); // Trigger change detection with OnPush
        } catch (err) {
            console.error(...new ExxComError(484996, scriptName, err).stamp());
        }
    }
    get categories() {
        const availableCategories = [];
        const allCategories = ['Blog', 'Case Studies', 'Whitepapers', 'eBooks'];

        // Check which categories have content
        const rows = get(this.relatedContentRow, 'rows', []);

        rows.forEach((row) => {
            if (Object.keys(row)[0] === 'related_content') {
                const relatedContent = get(row, 'related_content.related_content[0]', {});

                // Check for blog posts
                if (this.recommendedBlogPosts && this.recommendedBlogPosts.length > 0) {
                    if (!availableCategories.includes('Blog')) {
                        availableCategories.push('Blog');
                    }
                }

                // Check for case studies (newer case_study_entry or older case_studies)
                const caseStudyEntries = this.get(relatedContent, 'case_study_entry.case_study_entry_reference');
                const gatedCaseStudies = this.get(relatedContent, 'case_studies.gated_content_case_studies');
                if ((caseStudyEntries && caseStudyEntries.length > 0) || (gatedCaseStudies && gatedCaseStudies.length > 0)) {
                    if (!availableCategories.includes('Case Studies')) {
                        availableCategories.push('Case Studies');
                    }
                }

                // Check for whitepapers
                const whitepapers = this.get(relatedContent, 'whitepapers.gated_content_whitepapers');
                if (whitepapers && whitepapers.length > 0) {
                    if (!availableCategories.includes('Whitepapers')) {
                        availableCategories.push('Whitepapers');
                    }
                }

                // Check for ebooks
                const ebooks = this.get(relatedContent, 'ebooks.gated_content_ebooks');
                if (ebooks && ebooks.length > 0) {
                    if (!availableCategories.includes('eBooks')) {
                        availableCategories.push('eBooks');
                    }
                }
            }
        });

        return availableCategories.length > 0 ? availableCategories : allCategories;
    }

    getCategoryPosts(entry: any, categoryName?: string) {
        const chosenCategoryName = categoryName ? categoryName : this.activeCategoryName;

        try {
            if (chosenCategoryName == 'Blog') {
                return this.recommendedBlogPosts;
            } else if (chosenCategoryName == 'Case Studies') {
                // Check for case study entry references first
                const caseStudyEntries = this.get(entry, 'related_content[0].case_study_entry.case_study_entry_reference');
                // Only use case_study_entry if the references are actually populated with full data (has title/url/etc)
                if (caseStudyEntries && caseStudyEntries.length > 0 && caseStudyEntries[0].title && caseStudyEntries[0].landing_page_url) {
                    return caseStudyEntries;
                }
                // Fall back to existing gated content structure (old case_studies)
                return this.get(entry, 'related_content[0].case_studies.gated_content_case_studies') || [];
            } else if (chosenCategoryName == 'eBooks') {
                return this.get(entry, 'related_content[0].ebooks.gated_content_ebooks') || [];
            } else {
                return this.get(entry, 'related_content[0].whitepapers.gated_content_whitepapers') || [];
            }
        } catch (err) {
            console.error(...new ExxComError(484997, scriptName, err).stamp());
        }
    }
}
